import React, { useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';

import { CheckCircleIcon, UserIcon, IdCardIcon, ShieldIcon } from '../../utils/IconWrapper';
import RegistrationStep1 from './registration-step-1';
import RegistrationStep2 from './registration-step-2';
import RegistrationStep3 from './registration-step-3';

const WizardContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: var(--bg-secondary);
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const WizardHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #0d9488;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const StepsIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  margin-bottom: 3rem;
`;

const StepItem = styled.div<{ active: boolean; completed: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  opacity: ${props => props.active || props.completed ? 1 : 0.5};
  transition: all 0.3s ease;
`;

const StepIcon = styled.div<{ active: boolean; completed: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  transition: all 0.3s ease;
  
  ${props => props.completed ? `
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    color: white;
    transform: scale(1.1);
  ` : props.active ? `
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    color: white;
    transform: scale(1.1);
  ` : `
    background: var(--bg-primary);
    color: var(--text-secondary);
  `}
`;

const StepLabel = styled.span<{ active: boolean; completed: boolean }>`
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.active || props.completed ? '#0d9488' : 'var(--text-secondary)'};
`;

const StepContent = styled.div`
  min-height: 500px;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #0d9488 0%, #14b8a6 100%);
    color: white;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(13, 148, 136, 0.4);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
      transform: none;
    }
  ` : `
    background: var(--bg-primary);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    
    &:hover {
      background: var(--bg-secondary);
    }
  `}
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: var(--border-color);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  border-radius: 2px;
`;

interface RegistrationWizardProps {
  onComplete?: (data: any) => void;
  onCancel?: () => void;
}

const RegistrationWizard: React.FC<RegistrationWizardProps> = ({ 
  onComplete, 
  onCancel 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);
  const [formData, setFormData] = useState<any>({});

  const steps = [
    {
      number: 1,
      title: 'Account',
      icon: UserIcon,
      description: 'Crea il tuo account'
    },
    {
      number: 2,
      title: 'Profilo',
      icon: IdCardIcon,
      description: 'Completa il profilo'
    },
    {
      number: 3,
      title: 'Sicurezza',
      icon: ShieldIcon,
      description: 'Impostazioni sicurezza'
    }
  ];

  const handleStepComplete = (stepData: any) => {
    setFormData({ ...formData, ...stepData });
    setCompletedSteps([...completedSteps, currentStep]);
    
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Registrazione completata
      onComplete?.(formData);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progressPercentage = ((currentStep - 1) / (steps.length - 1)) * 100;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <RegistrationStep1
            onNext={handleStepComplete}
            initialData={formData}
          />
        );
      case 2:
        return (
          <RegistrationStep2
            onNext={handleStepComplete}
            onPrevious={handlePreviousStep}
            initialData={formData}
          />
        );
      case 3:
        return (
          <RegistrationStep3
            onNext={handleStepComplete}
            onPrevious={handlePreviousStep}
            initialData={formData}
          />
        );
      default:
        return null;
    }
  };

  const getStepIcon = (step: any, isCompleted: boolean) => {
    if (isCompleted) return <CheckCircleIcon />;
    
    switch (step.number) {
      case 1:
        return <UserIcon />;
      case 2:
        return <IdCardIcon />;
      case 3:
        return <ShieldIcon />;
      default:
        return <UserIcon />;
    }
  };

  return (
    <WizardContainer>
      <WizardHeader>
        <Title>
          🏡 Unisciti a J-O-H
        </Title>
        <Subtitle>
          Registrati per accedere a <strong>sconti esclusivi</strong> e restare aggiornato sui <strong>nuovi immobili</strong>
        </Subtitle>
        
        <ProgressBar>
          <ProgressFill
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </ProgressBar>
      </WizardHeader>

      <StepsIndicator>
        {steps.map((step) => {
          const isActive = step.number === currentStep;
          const isCompleted = completedSteps.includes(step.number);
          
          return (
            <StepItem 
              key={step.number} 
              active={isActive} 
              completed={isCompleted}
            >
              <StepIcon active={isActive} completed={isCompleted}>
                {getStepIcon(step, isCompleted)}
              </StepIcon>
              <StepLabel active={isActive} completed={isCompleted}>
                {step.title}
              </StepLabel>
            </StepItem>
          );
        })}
      </StepsIndicator>

      <StepContent>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </StepContent>

      {onCancel && (
        <NavigationButtons>
          <Button variant="secondary" onClick={onCancel}>
            Annulla
          </Button>
        </NavigationButtons>
      )}
    </WizardContainer>
  );
};

export default RegistrationWizard; 